/**
 * Shop Life: KanCraft's #shop-life Slack channel as a card at the end of the station strip
 * (App.jsx StationActivityBanner). Mike, 2026-10-29: "display posts from the #shop-life channel
 * in the KanCraft workspace here" (the empty space right of the station cards).
 *
 * Desktop strip only (App.jsx leaves it out of the stacked phone layout). Reads only while the strip is
 * open (`active`) and the tab is visible, once a minute. Every post
 * in the channel shows (people and JobQ Bot's floor notices alike). A row opens to the full text,
 * bigger photos and "Open in Slack"; a photo opens large. Logic: features/shopLife/shopLife.js.
 */
import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import {
  FEED_POLL_MS, loadShopLifeFeed, loadPhoto, timeAgo, fullTime, isNewPost, safeHref, initials, plainText,
} from '../features/shopLife/shopLife';

const MUTED = 'var(--jq-text-muted, #666)';
const TEXT = 'var(--jq-text-primary, #333)';
const BORDER = 'var(--jq-border, #ccc)';
const CARD = 'var(--jq-bg-card, #fff)';
const ACCENT = '#4a9eff';

export function Segments({ segments }) {
  return (segments || []).map((s, i) => {
    switch (s.t) {
      case 'bold': return <strong key={i}>{s.v}</strong>;
      case 'italic': return <em key={i}>{s.v}</em>;
      case 'strike': return <s key={i}>{s.v}</s>;
      case 'code': return <code key={i} style={{ fontSize: '0.9em' }}>{s.v}</code>;
      case 'mention': return <span key={i} style={{ color: ACCENT, fontWeight: 600 }}>{s.v}</span>;
      case 'link': {
        const href = safeHref(s.href);
        if (!href) return <span key={i}>{s.v}</span>;
        return (
          <a key={i} href={href} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
            {s.v}
          </a>
        );
      }
      default: return <span key={i}>{s.v}</span>;
    }
  });
}

// retryKey changes with every feed read, so a photo that failed (e.g. Slack was slow) is tried again then.
function SlackPhoto({ file, size = 'small', height, onOpen, retryKey }) {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (src) return undefined;
    let alive = true;
    loadPhoto(file.id, size).then((url) => {
      if (!alive) return;
      if (url) {
        setSrc(url);
        setFailed(false);
      } else {
        setFailed(true);
      }
    });
    return () => { alive = false; };
  }, [file.id, size, retryKey, src]);

  const box = { height, borderRadius: 4, flexShrink: 0, border: `1px solid ${BORDER}` };
  if (failed) {
    return (
      <span title={`${file.name}: could not load`} style={{ ...box, width: height, display: 'inline-flex',
        alignItems: 'center', justifyContent: 'center', color: MUTED, fontSize: '0.7rem' }}>
        <i className="fas fa-image" />
      </span>
    );
  }
  if (!src) return <span aria-label={`Loading ${file.name}`} style={{ ...box, width: height, background: 'rgba(128,128,128,0.15)' }} />;
  if (size === 'large') {
    return <img src={src} alt={file.name} style={{ maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain' }} />;
  }
  return (
    <img
      src={src}
      alt={file.name}
      title={`${file.name}: click to enlarge`}
      tabIndex={0}
      style={{ ...box, maxWidth: height * 3, objectFit: 'cover', cursor: 'zoom-in' }}
      onClick={(e) => { e.stopPropagation(); onOpen(file); }}
      onKeyDown={(e) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        e.stopPropagation();
        onOpen(file);
      }}
    />
  );
}

function Avatar({ author }) {
  const size = 22;
  if (author && author.avatar) {
    return <img src={author.avatar} alt="" style={{ width: size, height: size, borderRadius: '50%', flexShrink: 0 }} />;
  }
  return (
    <span aria-hidden style={{ width: size, height: size, borderRadius: '50%', flexShrink: 0, background: '#6c757d',
      color: '#fff', fontSize: '0.6rem', fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
      {initials(author && author.name)}
    </span>
  );
}

function PostRow({ post, open, onToggle, onOpenPhoto, now, retryKey }) {
  const author = post.author || {};
  const photos = (post.files || []).filter((f) => f.kind === 'image');
  const others = (post.files || []).filter((f) => f.kind !== 'image');
  const fresh = isNewPost(post, now);
  return (
    <div
      role="button"
      tabIndex={0}
      aria-expanded={open}
      data-testid="shop-life-post"
      onClick={onToggle}
      onKeyDown={(e) => {
        // Only the row itself: Enter on a link or a photo inside it does what that does.
        if (e.target !== e.currentTarget || (e.key !== 'Enter' && e.key !== ' ')) return;
        e.preventDefault();
        onToggle();
      }}
      style={{
        display: 'flex', gap: 6, padding: '4px 6px', cursor: 'pointer',
        borderBottom: `1px solid ${BORDER}`,
        borderLeft: `3px solid ${fresh ? ACCENT : 'transparent'}`,
      }}
    >
      <Avatar author={author} />
      <div style={{ flex: '1 1 auto', minWidth: 0 }}>
        <div style={{ fontSize: '0.72rem', lineHeight: 1.3 }}>
          <span style={{ fontWeight: 700, color: author.is_bot ? MUTED : TEXT }}>{author.name || 'Someone'}</span>
          <span title={fullTime(post.posted_at)} style={{ color: MUTED, marginLeft: 6 }}>{timeAgo(post.posted_at, now)}</span>
          {post.reply_count > 0 && (
            <span style={{ color: MUTED, marginLeft: 6 }}>
              · {post.reply_count} repl{post.reply_count === 1 ? 'y' : 'ies'}
            </span>
          )}
        </div>
        <div
          style={{
            fontSize: '0.76rem', color: TEXT, lineHeight: 1.35, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere',
            ...(open ? {} : { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }),
          }}
        >
          <Segments segments={post.segments} />
          {!plainText(post.segments) && !photos.length && others.length > 0 && (
            <span style={{ color: MUTED }}>{others.map((f) => f.name).join(', ')}</span>
          )}
        </div>
        {open && (
          <div style={{ marginTop: 4 }}>
            {photos.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 4 }}>
                {photos.map((f) => <SlackPhoto key={f.id} file={f} height={110} onOpen={onOpenPhoto} retryKey={retryKey} />)}
              </div>
            )}
            {others.length > 0 && (
              <div style={{ fontSize: '0.7rem', color: MUTED }}>Files: {others.map((f) => f.name).join(', ')}</div>
            )}
            <a href={post.permalink} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
              style={{ fontSize: '0.7rem' }}>
              Open in Slack <i className="fas fa-external-link-alt" style={{ fontSize: '0.6rem' }} />
            </a>
          </div>
        )}
      </div>
      {!open && photos.length > 0 && (
        <div style={{ display: 'flex', gap: 4, alignItems: 'flex-start', flexShrink: 0 }}>
          {photos.slice(0, 2).map((f) => <SlackPhoto key={f.id} file={f} height={34} onOpen={onOpenPhoto} retryKey={retryKey} />)}
          {photos.length > 2 && <span style={{ fontSize: '0.7rem', color: MUTED, alignSelf: 'center' }}>+{photos.length - 2}</span>}
        </div>
      )}
    </div>
  );
}

export default function ShopLifeFeed({ active = true }) {
  const [feed, setFeed] = useState(null);
  const [error, setError] = useState(null);
  const [openTs, setOpenTs] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;
    const load = async () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      const r = await loadShopLifeFeed();
      if (cancelled) return;
      setNow(Date.now());
      if (r.ok) {
        setFeed(r.data);
        setError(null);
      } else {
        setError(r.message);
      }
    };
    load();
    const timer = setInterval(load, FEED_POLL_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [active]);

  const posts = (feed && feed.posts) || [];
  const notUpdating = Boolean(feed && (feed.stale || error));

  let body;
  if (!feed && error) body = <div style={{ padding: 8, fontSize: '0.78rem', color: MUTED }}>{error}</div>;
  else if (!feed) body = <div style={{ padding: 8, fontSize: '0.78rem', color: MUTED }}>Loading…</div>;
  else if (!posts.length) body = <div style={{ padding: 8, fontSize: '0.78rem', color: MUTED }}>No posts yet.</div>;
  else {
    body = posts.map((p) => (
      <PostRow
        key={p.ts}
        post={p}
        now={now}
        retryKey={feed.fetched_at}
        open={openTs === p.ts}
        onToggle={() => setOpenTs((cur) => (cur === p.ts ? null : p.ts))}
        onOpenPhoto={(f) => setPhoto(f)}
      />
    ));
  }

  return (
    <div
      data-testid="shop-life"
      className="rounded border mb-2"
      style={{
        // 240 px basis: fits beside the seven station cards from ~1536 px wide; narrower, it takes its own row.
        display: 'flex', flexDirection: 'column', background: CARD, borderColor: BORDER,
        flex: '1 1 240px', minWidth: 220, minHeight: 120, alignSelf: 'stretch',
      }}
    >
      <div className="d-flex align-items-center justify-content-between"
        style={{ padding: '4px 8px', borderBottom: `1px solid ${BORDER}` }}>
        <span style={{ fontWeight: 700, fontSize: '0.85rem', color: TEXT }}>
          <i className="fab fa-slack me-1" style={{ color: MUTED }} />Shop Life
        </span>
        <span className="d-flex align-items-center gap-2" style={{ fontSize: '0.7rem' }}>
          {notUpdating && (
            <span title={error || 'Slack did not answer; showing the last posts JobQ read.'} style={{ color: '#b8860b' }}>
              <i className="fas fa-exclamation-triangle me-1" />not updating
            </span>
          )}
          {feed && feed.channel_url && (
            <a href={feed.channel_url} target="_blank" rel="noopener noreferrer">#{feed.channel || 'shop-life'}</a>
          )}
        </span>
      </div>
      {/* The list is absolutely placed so the card takes the station cards' height instead of setting it. */}
      <div style={{ position: 'relative', flex: '1 1 auto', minHeight: 0 }}>
        <div style={{ position: 'absolute', inset: 0, overflowY: 'auto' }}>{body}</div>
      </div>
      <Modal show={Boolean(photo)} onHide={() => setPhoto(null)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1rem' }}>{photo && photo.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ textAlign: 'center' }}>
          {photo && <SlackPhoto file={photo} size="large" height={240} onOpen={() => {}} retryKey={feed && feed.fetched_at} />}
        </Modal.Body>
      </Modal>
    </div>
  );
}
